import React, { useState, useEffect } from 'react';
import { Button, Stack, TablePagination, Tooltip, Paper, Typography } from '@mui/material';
import { pagination } from 'Components/Common/Tables/Tables';
import { ColumnType } from 'Components/Common/Tables/TableHeaders';
import TableFiltering from 'Components/Reusable/TableFiltering/TableFiltering';
import FilterListButton from 'Components/Reusable/FilterList/FilterList';
import TrimTables from 'Components/Reusable/TrimTables/TrimTables';
import CustomTable from 'Components/Common/Tables/CustomTable/CustomTable';
import { Add } from '@mui/icons-material';
import useMissionSundayMaterialActions, { MissionMaterialData } from 'hooks/useMissionSundayMaterial';
import { OrderType, StateType } from 'contexts/intialstates/Types';
import { sortTable } from 'Components/Common/Tables/sortTable';
import OrderTypeArrow from 'Components/Common/Tables/OrderTypeArrow';
import { convertDate } from 'helpers/dayAndTime';
import CreateEditMissionMaterial from './CreateEditMissionMaterial';

const missionMaterialHead: ColumnType[] = [
	{
		id: 's/n',
		label: 'S/N',
		minWidth: 70,
		align: 'center',
	},
	{
		id: 'itemCode',
		label: 'Item Code',
		minWidth: 200,
		align: 'center',
		filterType: 'text',
	},
	{
		id: 'description',
		label: 'Description',
		minWidth: 200,
		align: 'center',
		filterType: 'text',
	},
	{
		id: 'createdBy',
		label: 'Created By',
		minWidth: 170,
		align: 'center',
		filterType: 'text',
	},
	{
		id: 'updatedBy',
		label: 'Updated By',
		minWidth: 170,
		align: 'center',
		filterType: 'text',
	},
	{
		id: 'created',
		label: 'Date Created',
		minWidth: 170,
		align: 'center',
		filterType: 'date',
	},
	{
		id: 'updated',
		label: 'Date Updated',
		minWidth: 170,
		align: 'center',
		filterType: 'date',
	},
];

const MissionSundayMaterial = () => {
	const { getMissionMaterial } = useMissionSundayMaterialActions();
	const [trimList, setTrimList] = useState<ColumnType[]>([]);
	const [filterPayload, setFilterPayload] = useState<Array<any>>([]);
	const [reload, setReload] = useState(false);
	const [missionMaterial, setMissionMaterial] = useState<StateType<MissionMaterialData>>({
		data: null,
		error: null,
		loading: false,
	});
	const [page, setPage] = useState<number>(0);
	const [pageSize, setPageSize] = useState<number>(10);
	const [orderType, setOrderType] = useState<OrderType>('asc');
	const [label, setLabel] = useState<string>('');
	const [orderBy, setOderBy] = useState<string>('');
	const [open, setOpen] = useState(false);
	const [selectedData, setSelectedData] = useState();

	useEffect(() => {
		getMissionMaterial({ missionMaterial, setMissionMaterial, page, pageSize, filterPayload, orderBy });
	}, [page, pageSize, reload, orderBy]);

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPageSize(+event.target.value);
		setPage(0);
	};

	const handleDrag = (reOrderedData: ColumnType[]) => {
		setTrimList(reOrderedData);
	};

	const handleResize = (resizedData: ColumnType[]) => {
		setTrimList(resizedData);
	};

	const getColumnData = () => {
		return trimList.map((column: ColumnType) => {
			return {
				...column,
				data: (
					<>
						<Tooltip key={column.id} title={`Sort by ${column.label}`}>
							<span
								style={{ cursor: 'pointer' }}
								onClick={() =>
									sortTable({
										column: column.id,
										setLabel,
										setOderBy,
										setOrderType,
										orderType,
										label,
									})
								}
							>
								{column.label}
								{column.id == label && <OrderTypeArrow orderType={orderType} />}
							</span>
						</Tooltip>
						<TableFiltering
							field={column.id}
							filterType={column.filterType}
							list={column.list}
							setFilterPayload={setFilterPayload}
							filterPayload={filterPayload}
							reload={reload}
							setIsReload={setReload}
							setPage={setPage}
							page={page}
						/>
					</>
				),
			};
		});
	};

	const getTableData = () => {
		return missionMaterial?.data?.body?.length
			? missionMaterial?.data.body.map((row: any, index: number) => {
					return {
						's/n': page * pageSize + index + 1,
						id: row?.id,
						itemCode: row?.itemCode || 'N/A',
						description: row?.description || 'N/A',
						created: row?.created ? `${convertDate(row?.created)}` : 'N/A',
						createdBy: row?.createdBy?.name || 'N/A',
						updated: row?.updated ? `${convertDate(row?.updated)}` : 'N/A',
						updatedBy: row?.updatedBy?.name || 'N/A',
					};
			  })
			: [];
	};

	const handleToggle = () => {
		setOpen(!open);
		if (open) {
			setSelectedData(undefined);
		}
	};

	const handleEdit = (row: any) => {
		setOpen(!open);
		setSelectedData(row);
	};

	return (
		<>
			<Stack direction={'row'} justifyContent={'space-between'} mb={3}>
				<Stack direction={'column'}>
					<Typography variant="h5" sx={{ color: '#1976D2' }}>
						Mission Sunday Material
					</Typography>
					<Stack>Create and manage Mission Sunday Materials here.</Stack>
				</Stack>
				<Stack direction={'row'} alignItems={'center'}>
					<TrimTables
						name="missionMaterialHead"
						list={missionMaterialHead}
						trimList={trimList}
						setTrimList={setTrimList}
					/>
					<Button
						sx={{ textTransform: 'none', mr: 2 }}
						onClick={handleToggle}
						variant="contained"
						startIcon={<Add />}
					>
						Add
					</Button>
					<FilterListButton
						setFilterPayload={setFilterPayload}
						filterPayload={filterPayload}
						reload={reload}
						setIsReload={setReload}
						setPage={setPage}
						page={page}
					/>
				</Stack>
			</Stack>
			<Paper sx={{ width: '100%', overflow: 'hidden' }}>
				<CustomTable
					loading={missionMaterial?.loading}
					columnData={getColumnData()}
					tableData={getTableData()}
					handleDrag={handleDrag}
					handleResize={handleResize}
					emptyMessage={'No mission sunday material available'}
					handleRowClick={(row) => handleEdit(row as any)}
				/>
				{(missionMaterial?.data?.body.length as number) > 0 && (
					<TablePagination
						rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
						count={missionMaterial?.data?.totalRecords as number}
						rowsPerPage={pageSize}
						page={page}
						sx={pagination}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				)}
			</Paper>
			{open && (
				<CreateEditMissionMaterial
					visible={open}
					handleClose={handleToggle}
					reload={reload}
					setReload={setReload}
					selectedData={selectedData}
				/>
			)}
		</>
	);
};

export default MissionSundayMaterial;
